import type {
  NameSanitizationRule,
  PreviewNameSanitizationResponse,
  RunNameCleaningResponse,
} from '@shared/schemas';
import { all, call, getContext, put, takeEvery, takeLatest } from 'redux-saga/effects';

import { classifyInfraError } from '@/utils/infra-error';

import type { NameRulesGateway } from '../infrastructure/name-rules.gateway';
import {
  clearRequested,
  createRequested,
  deleteRequested,
  loadRequested,
  previewRequested,
  runRequested,
} from './name-rules.actions';
import { nameRulesActions } from './name-rules.slice';

/**
 * The backend's own message when it has one, and here it usually does: a 400
 * names the reason ("the apostrophe belongs to real names", "the blocklist is
 * empty"), which is the whole point of refusing rather than silently accepting.
 */
function toMessage(e: unknown, fallback: string): string {
  return classifyInfraError(e, fallback).message;
}

/** WORKER: initial load (drives the first-mount spinner). */
function* loadWorker() {
  const gateway: NameRulesGateway = yield getContext('nameRulesGateway');
  yield put(nameRulesActions.loadStarted());
  try {
    const rules: NameSanitizationRule[] = yield call(gateway.loadRules);
    yield put(nameRulesActions.loadSucceeded(rules));
  } catch {
    yield put(nameRulesActions.loadFailed());
  }
}

/** WORKER: add one entry to the blocklist. */
function* createWorker(action: ReturnType<typeof createRequested>) {
  const gateway: NameRulesGateway = yield getContext('nameRulesGateway');
  yield put(nameRulesActions.mutationStarted());
  try {
    const rule: NameSanitizationRule = yield call(gateway.createRule, action.payload);
    yield put(nameRulesActions.ruleCreated(rule));
  } catch (e) {
    yield put(nameRulesActions.mutationFailed(toMessage(e, 'Could not add the rule.')));
  }
}

/** WORKER: remove one entry. Not retroactive: cleaned names stay cleaned. */
function* deleteWorker(action: ReturnType<typeof deleteRequested>) {
  const gateway: NameRulesGateway = yield getContext('nameRulesGateway');
  yield put(nameRulesActions.mutationStarted());
  try {
    yield call(gateway.deleteRule, action.payload);
    yield put(nameRulesActions.ruleDeleted(action.payload));
  } catch (e) {
    yield put(nameRulesActions.mutationFailed(toMessage(e, 'Could not delete the rule.')));
  }
}

/** WORKER: empty one whole list. */
function* clearWorker(action: ReturnType<typeof clearRequested>) {
  const gateway: NameRulesGateway = yield getContext('nameRulesGateway');
  yield put(nameRulesActions.mutationStarted());
  try {
    yield call(gateway.clearRules, action.payload);
    yield put(nameRulesActions.rulesCleared(action.payload));
  } catch (e) {
    yield put(nameRulesActions.mutationFailed(toMessage(e, 'Could not clear the list.')));
  }
}

/**
 * WORKER: the test field.
 *
 * `takeLatest`, because the field previews as the admin types: an earlier, slower
 * answer must never land after a later one and show the cleaning of a name that is
 * no longer on screen.
 */
function* previewWorker(action: ReturnType<typeof previewRequested>) {
  const gateway: NameRulesGateway = yield getContext('nameRulesGateway');
  if (action.payload.trim().length === 0) {
    yield put(nameRulesActions.previewCleared());
    return;
  }
  try {
    const preview: PreviewNameSanitizationResponse = yield call(gateway.preview, action.payload);
    yield put(nameRulesActions.previewLoaded(preview));
  } catch {
    // Silent: the test field is an aid, and an error banner over a typing field
    // would fire on every half-typed name.
    yield put(nameRulesActions.previewCleared());
  }
}

/** WORKER: the retroactive pass over the whole database. Not reversible. */
function* runWorker() {
  const gateway: NameRulesGateway = yield getContext('nameRulesGateway');
  yield put(nameRulesActions.runStarted());
  try {
    const metrics: RunNameCleaningResponse = yield call(gateway.run);
    yield put(nameRulesActions.runSucceeded(metrics));
  } catch (e) {
    yield put(nameRulesActions.runFailed(toMessage(e, 'The cleaning run failed.')));
  }
}

/** WATCHERS. */
export function* nameRulesSaga() {
  yield all([
    takeLatest(loadRequested.type, loadWorker),
    takeEvery(createRequested.type, createWorker),
    takeEvery(deleteRequested.type, deleteWorker),
    takeLatest(clearRequested.type, clearWorker),
    takeLatest(previewRequested.type, previewWorker),
    takeLatest(runRequested.type, runWorker),
  ]);
}
